// src/pages/UploadPage.js
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload, FileText, CheckCircle, X, ArrowRight, BarChart2, Clock, Trash2, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { uploadReport, getPendingFiles, cancelPendingFile, getReports } from '../utils/api';

function PendingBanner({ qc }) {
  const { data } = useQuery({
    queryKey: ['pendingFiles'],
    queryFn:  getPendingFiles,
    refetchInterval: 8000,
  });

  const cancelMutation = useMutation({
    mutationFn: cancelPendingFile,
    onSuccess: () => {
      toast.success('Pending file removed');
      qc.invalidateQueries({ queryKey: ['pendingFiles'] });
    },
    onError: () => toast.error('Failed to cancel'),
  });

  const pending = data?.pendingFiles || [];
  if (pending.length === 0) return null;

  return (
    <div style={{
      marginBottom: 20,
      borderRadius: 12,
      border: '1px solid rgba(226,185,111,0.35)',
      background: 'rgba(226,185,111,0.06)',
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 18px',
        display: 'flex', alignItems: 'center', gap: 10,
        borderBottom: '1px solid rgba(226,185,111,0.2)',
        background: 'rgba(226,185,111,0.08)',
      }}>
        <Clock size={14} style={{ color: 'var(--gold)' }} />
        <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--gold)' }}>
          Waiting for Partner Files
        </span>
        <span style={{
          fontSize: 11, padding: '1px 8px', borderRadius: 20,
          background: 'rgba(226,185,111,0.2)', color: 'var(--gold)',
        }}>
          {pending.length} pending
        </span>
      </div>

      {pending.map(pf => (
        <div key={pf.id} style={{
          padding: '12px 18px',
          display: 'flex', alignItems: 'center', gap: 12,
          borderBottom: '1px solid rgba(226,185,111,0.1)',
          fontSize: 13,
        }}>
          <FileText size={14} style={{ color: 'var(--gold)', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>
              {pf.filename}
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
              Prompt: <span style={{ color: 'var(--gold)' }}>{pf.promptKey}</span>
              {' · '}Uploaded {new Date(pf.uploadedAt).toLocaleTimeString()}
            </div>
          </div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => cancelMutation.mutate(pf.id)}
            disabled={cancelMutation.isPending}
            title="Remove this pending file"
          >
            <Trash2 size={11} /> Cancel
          </button>
        </div>
      ))}

      <div style={{ padding: '10px 18px', fontSize: 12, color: 'var(--text-muted)' }}>
        Upload the partner file(s) above and the analysis will run automatically.
      </div>
    </div>
  );
}

export default function UploadPage() {
  const [file, setFile] = useState(null);
  const [done, setDone] = useState(null);
  const [processing, setProcessing] = useState(null); // { runId, filename, startedAt, expectedReports }
  const navigate = useNavigate();
  const qc = useQueryClient();
  const pollRef = useRef(null);

  // Poll reports every 5s while a background job is running
  useEffect(() => {
    if (!processing) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    const MAX_POLL_MS = 15 * 60 * 1000; // 15 minutes hard timeout
    pollRef.current = setInterval(async () => {
      try {
        const data = await getReports(200);
        const reports = data?.reports || data || [];
        const matches = reports.filter(r =>
          r.runId === processing.runId ||
          (r.inputFilename === processing.filename && new Date(r.processedAt) >= processing.startedAt)
        );
        const expectedReports = processing.expectedReports || 1;
        const elapsed = Date.now() - processing.startedAt.getTime();
        // Resolve if we have all expected reports, OR if we have ≥1 and the timeout has passed
        const shouldResolve = matches.length >= expectedReports || (matches.length >= 1 && elapsed > MAX_POLL_MS);
        if (shouldResolve) {
          const primary = matches.find(r => /Grouping of Expense with CFO Commentary/i.test(r.promptName || '')) || matches[0];
          clearInterval(pollRef.current);
          pollRef.current = null;
          setProcessing(null);
          setDone({
            status: 'success',
            filename: primary.inputFilename,
            outputFilename: primary.outputFilename,
            outputFilenames: matches.map(r => r.outputFilename).filter(Boolean),
            company: primary.company,
            tokensUsed: matches.reduce((sum, r) => sum + (r.tokensUsed || 0), 0),
          });
          toast.success(matches.length >= expectedReports ? 'Analysis complete — reports saved!' : 'Analysis complete — some reports saved.');
          qc.invalidateQueries({ queryKey: ['reports'] });
        }
      } catch (err) {
        toast.error(err.response?.data?.error || 'Could not refresh analyzed reports');
      }
    }, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [processing, qc]);

  const mutation = useMutation({
    mutationFn: () => uploadReport(file, false),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['pendingFiles'] });
      if (data.status === 'pending') {
        setDone(data);
      } else if (data.status === 'success') {
        setDone(data);
        toast.success('Analysis complete — report saved!');
        if (data.warnings?.length) {
          data.warnings.forEach(w => toast(w, { icon: '⚠️', duration: 8000 }));
        }
      } else if (data.status === 'processing') {
        // Background processing — poll until every matched prompt saves a report
        setProcessing({
          runId: data.runId,
          filename: data.filename || file?.name,
          startedAt: new Date(),
          expectedReports: data.expectedReports || data.prompts?.length || 1,
        });
      } else {
        toast.error('Processing failed: ' + (data.error || 'Unknown error'));
      }
    },
    onError: (err) => toast.error(err.response?.data?.error || err.message),
  });

  const onDrop = useCallback((accepted) => {
    if (accepted[0]) { setFile(accepted[0]); setDone(null); }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
    maxSize: 20 * 1024 * 1024,
  });

  function reset() { setFile(null); setDone(null); setProcessing(null); }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Upload Report</h1>
        <p className="page-subtitle">
          Upload a QuickBooks export — the system automatically analyzes it and saves the output report.
        </p>
      </div>

      {/* ── Pending files banner ── */}
      <PendingBanner qc={qc} />

      {/* ── Drop zone ── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">Select File</div>

        <div {...getRootProps()} className={`dropzone ${isDragActive ? 'drag-over' : ''}`}>
          <input {...getInputProps()} />
          <div className="dropzone-icon"><Upload size={36} /></div>
          {file ? (
            <>
              <div className="dropzone-label" style={{ color: 'var(--gold)' }}>
                <FileText size={16} style={{ display: 'inline', marginRight: 6 }} />
                {file.name}
              </div>
              <div className="dropzone-sub">{(file.size / 1024).toFixed(1)} KB — click or drop to replace</div>
            </>
          ) : (
            <>
              <div className="dropzone-label">Drop your QuickBooks export here</div>
              <div className="dropzone-sub">Supports .xlsx, .xls, .csv, .pdf — max 20MB</div>
            </>
          )}
        </div>

        {file && !done && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
            >
              {mutation.isPending
                ? <><span className="spinner" /> Analyzing…</>
                : <><Upload size={15} /> Analyze Report</>}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={reset}>
              <X size={13} /> Clear
            </button>
          </div>
        )}
      </div>

      {/* ── Processing indicator (upload in flight) ── */}
      {mutation.isPending && (
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span className="spinner" />
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Uploading &amp; queuing…</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
              Sending file to the server.
            </div>
          </div>
        </div>
      )}

      {/* ── Background processing (Claude is running) ── */}
      {processing && !mutation.isPending && (
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 14, borderTop: '3px solid var(--gold)' }}>
          <span className="spinner" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Claude is analyzing your report…</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
              This takes 30–90 seconds for expense reports, or up to 5 minutes for portfolio dashboards. The page will update automatically when done.
            </div>
            <div style={{ color: 'var(--gold)', fontSize: 12, marginTop: 6 }}>
              File: <strong>{processing.filename}</strong>
            </div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={reset} style={{ flexShrink: 0 }}>
            <X size={12} /> Cancel
          </button>
        </div>
      )}

      {/* ── Pending / waiting for 2nd file ── */}
      {done && done.status === 'pending' && (
        <div className="card" style={{ borderColor: 'rgba(226,185,111,0.4)', borderTop: '3px solid var(--gold)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <Clock size={28} style={{ color: 'var(--gold)', flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>
                File saved — waiting for partner file
              </div>

              <div style={{
                background: 'var(--navy-3)', borderRadius: 8, padding: '10px 14px',
                marginBottom: 14, fontSize: 13,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <FileText size={13} style={{ color: 'var(--gold)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-muted)' }}>Uploaded:</span>
                  <span style={{ fontWeight: 600, color: 'var(--gold)' }}>{done.filename}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertCircle size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-muted)' }}>Still needed:</span>
                  <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                    {(done.waitingFor || []).join(', ')}
                  </span>
                </div>
              </div>

              <div style={{
                background: 'rgba(226,185,111,0.08)', border: '1px solid rgba(226,185,111,0.25)',
                borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <BarChart2 size={14} style={{ color: 'var(--gold)', flexShrink: 0 }} />
                <span>
                  Upload <strong style={{ color: 'var(--gold)' }}>{(done.waitingFor || []).join(' and ')}</strong> to
                  trigger the <strong>{done.promptName}</strong> analysis automatically.
                </span>
              </div>

              <button className="btn btn-secondary btn-sm" onClick={reset}>
                Upload another file
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Success callout ── */}
      {done && done.status === 'success' && (
        <div className="card" style={{ borderColor: 'rgba(63,185,80,0.4)', borderTop: '3px solid var(--green)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <CheckCircle size={28} style={{ color: 'var(--green)', flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>
                Analysis complete!
              </div>

              {/* Input → Output */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                background: 'var(--navy-3)', borderRadius: 8, padding: '10px 14px',
                marginBottom: 14, fontSize: 13,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FileText size={13} style={{ color: 'var(--text-muted)' }} />
                  <span style={{ color: 'var(--text-muted)' }}>Input:</span>
                  <span style={{ fontWeight: 600, color: 'var(--text)' }}>{done.filename}</span>
                  {done.extraFilenames?.map((ef, i) => (
                    <React.Fragment key={i}>
                      <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>+</span>
                      <span style={{ fontWeight: 600, color: 'var(--text)' }}>{ef}</span>
                    </React.Fragment>
                  ))}
                </div>
                <ArrowRight size={14} style={{ color: 'var(--gold)', flexShrink: 0 }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FileText size={13} style={{ color: 'var(--gold)' }} />
                  <span style={{ color: 'var(--text-muted)' }}>Output:</span>
                  <span style={{ fontWeight: 600, color: 'var(--gold)' }}>
                    {done.outputFilename || 'Analysis report'}
                  </span>
                  {done.outputFilenames?.filter(name => name !== done.outputFilename).map((name, i) => (
                    <React.Fragment key={i}>
                      <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>+</span>
                      <span style={{ fontWeight: 600, color: 'var(--gold)' }}>{name}</span>
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {done.company && (
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
                  Company: <strong style={{ color: 'var(--text)' }}>{done.company}</strong>
                  {done.tokensUsed && (
                    <span style={{ marginLeft: 16 }}>
                      Tokens used: <strong style={{ color: 'var(--text)' }}>{done.tokensUsed.toLocaleString()}</strong>
                    </span>
                  )}
                </div>
              )}

              <div style={{
                background: 'rgba(226,185,111,0.08)', border: '1px solid rgba(226,185,111,0.25)',
                borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <BarChart2 size={14} style={{ color: 'var(--gold)', flexShrink: 0 }} />
                <span>
                  The analyzed report has been saved.
                  View it in the <strong style={{ color: 'var(--gold)' }}>Analyzed Reports</strong> section.
                </span>
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button className="btn btn-primary" onClick={() => navigate('/reports')}>
                  <BarChart2 size={14} /> View Analyzed Reports
                </button>
                <button className="btn btn-secondary btn-sm" onClick={reset}>
                  Upload another file
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Error ── */}
      {done && done.status === 'error' && (
        <div className="card" style={{ borderTop: '3px solid var(--red)' }}>
          <div style={{ color: 'var(--red)', fontSize: 13, padding: '4px 0' }}>
            Error: {done.error}
          </div>
          <button className="btn btn-secondary btn-sm" style={{ marginTop: 12 }} onClick={reset}>
            Try again
          </button>
        </div>
      )}
    </div>
  );
}
